import React from "react";
import { useMemo } from "react";
import { AllContent, Content, HeaderFooter, Img, List, Margin, Paragraph, PdfDefaults, Region, Group, Section, Segment, Table, ReportStructure, report_def } from "../core/pdf/types";
import { DesignerMessages } from "./types";
import { toMillimeters } from "../core/pdf/utils";
import { broadcast } from "../core/hooks/useChannel";
import { DesignerContext, DesignerState, useDesigner } from "./useDesigner";
import { Toolbar } from "./Toolbar";
import { Structure } from "./Structure";
import { Inspector } from "./Inspector";
import { StatusBar } from "./StatusBar";
import { PdfViewer, createPdfRenderer } from "./PdfViewer";
import { api } from "../api/api";
import { useGlobalHandler } from "../core/hooks/useGlobalHandler";
import { Toolbox } from "./Toolbox";


function BackgroundPdfLoader(props: {

}) {
	const designer = useDesigner();
	const structure = designer.structure;

	const [pdf, setPdf] = React.useState<any>();
	const [pages, setPages] = React.useState<any>();

	React.useEffect(() => {
		// if the layout for this report is static and we have a template pdf then need to load each page of the template 
		// and make sure there is a section per page of the template
		if (structure.layoutType === "static" && structure.template) {
			// load the template pdf
			const url = api.getDownloadSystemUrl(structure.template.blob);
			//const url = "/208_20140701.pdf";
			createPdfRenderer(url).then((result) => {
				setPdf(result);
			}).catch((e) => {
				debugger;

			});
		}

	}, [structure]);

	// loading of each of the individual pages
	React.useEffect(() => {
		if (!pdf) return;

		const pageCount = pdf.numPages;

		let pagesRemaining = pageCount;
		const pages = {} as any;

		// load each page first
		for(let i=1;i<=pageCount;i++) {
			pdf.getPage(i).then(function(page: any) {
				pagesRemaining--;
				pages[i] = page;
				checkComplete();
			});	
		}

		function checkComplete() {
			if (pagesRemaining === 0) {
				pages.length = pageCount;
				setPages(pages);

				// also store on the designer
				designer.bgPdfPages = pages;
			}
		}
			
		
	}, [pdf]);

	React.useEffect(() => {
		if (!pages) return;

		// finished loading all pages
		for(let pageNum=1;pageNum<=pages.length;pageNum++) {

			const page = pages[pageNum];

			// make sure the number of sections matches the page count
			const root = structure;
			if (root.sections.length < pageNum) {
				root.sections.push({
					id: designer.nextID(),
					type: "section",
					content: [],
				});
			}

			const section = root.sections[pageNum-1];

			const viewport = page.getViewport({scale: 1});
			section.width = viewport.width;
			section.height = viewport.height;
			section.pdfPageIndex = pageNum;

			designer.broadcast({
				type: DesignerMessages.REFRESH_ROOT,
			});

		}

	}, [pages]);


	return null;
}


export function PdfDesigner(props: {
	def: report_def,

	onSave: (structure: ReportStructure, title: string, close: boolean) => void,
	onPreview: (structure: ReportStructure, title: string) => void,
	onDelete: () => void,
	onCancel: () => void,
}) {

	const {
		onSave, onCancel, onDelete, onPreview, def,
	} = props;

	// getting annoying issues in dev where bundle updates randomly causing page to reload, so prevent that
	useGlobalHandler("beforeunload", (e: any) => {
		e.preventDefault();
		e.returnValue = "";
	});
	
	const state = useMemo(() => {
		const structure = def.data;

		// check for basics
		// are there defaults for this pdf yet?
		if (!structure.defaults) structure.defaults = PdfDefaults;

		const value: DesignerState = {
			channel: "pdf-designer",

			def: def,

			title: def.title,

			structure,

			bgPdfPages: undefined,
			
			nextID: function() {
				if (!this.structure.idCounter) this.structure.idCounter = 0;
				return ++this.structure.idCounter;
			},

			createStyle: function(baseFormats: any, overlayFormats?: any) {
				if (!overlayFormats) overlayFormats = {};
				const formats = {...baseFormats, ...overlayFormats};
				
				const result = {} as any;
			
				if (formats.margin) {
					this.updateStyleMargin(result, formats.margin);
				}

				this.updateStyleFont(result, formats);
			
				return result;
			},

			updateStyleFont: function(style: any, formats: any) {
				if (formats.fontSize) {
					style.fontSize = toMillimeters(formats.fontSize) + "mm";
				}

				if (formats.fontName) {
					style.fontFamily = formats.fontName;
				}

				if (formats.lineHeight) {
					style.lineHeight = formats.lineHeight;
				}

				if (formats.bold !== undefined) {
					style.bold = formats.bold;
				}
				if (formats.italic !== undefined) {
					style.italic = formats.italic;
				}
				if (formats.color !== undefined) {
					style.color = `rgb(${Math.floor(formats.color.red*255)}, ${Math.floor(formats.color.green*255)}, ${Math.floor(formats.color.blue*255)})`;
				}

				if (formats.align) {
					style.textAlign = formats.align;
				}

			},

			updateStyleMargin: function(style: any, margin: Margin) {
				style.paddingTop = toMillimeters(margin.top) + "mm";
				style.paddingRight = toMillimeters(margin.right) + "mm";
				style.paddingBottom = toMillimeters(margin.bottom) + "mm";
				style.paddingLeft = toMillimeters(margin.left) + "mm";
			},

			broadcast: function(msg: any) {
				broadcast(this.channel, msg);
			},
			broadcastToContent: function(id: number, msg: any) {
				broadcast(this.channel + "/" + id, msg);
			},

			setFocus: function(el?: Content) {
				if (this.focus === el) return;

				// if something focused let it know it is losing focus
				if (this.focus && this.focus.id) {
					this.broadcastToContent(
						this.focus.id,
						{
							type: DesignerMessages.BLUR,
						}
					);
				}

				this.focus = el;

				if (this.focus && this.focus.id) {
					this.broadcastToContent(
						this.focus.id,
						{
							type: DesignerMessages.FOCUS,
						}
					);
				}

				// notify anyone interested that the focused element has changed
				this.broadcast({
					type: DesignerMessages.FOCUS_CHANGED,
				});

			},

			getRef: function(contentID: number) {
				const msg = {
					type: DesignerMessages.GET_REF,
					ref: undefined,
				};
				this.broadcastToContent(contentID, msg);
				return msg.ref;
			},

			getContext: function(contentID: number) {
				const msg = {
					type: DesignerMessages.GET_CONTEXT,
					context: undefined,
				};
				this.broadcastToContent(contentID, msg);
				return msg.context;
			},

			save: function(close: boolean) {
				const data = prepareForSave(this);
				onSave(data, this.title, close);
			},

			preview: function() {
				const data = prepareForSave(this);
				onPreview(data, this.title);
			},

			delete: function() {
				onDelete();
			},

			cancel: function() {
				onCancel();
			},

			createItem: function(type: string, copyFrom?: Content) {
				// NOTE: only copy from elements of same type
				if (copyFrom && copyFrom.type !== type) copyFrom = undefined;

				if (type === "p") {
					return {
						...copyFrom,
						id: this.nextID(),
						type,
						segments: [],
						html: "",
					};

				} else if (type === "list") {
					const p = this.createItem("p") as Paragraph;

					const list: List = {
						id: this.nextID(),
						type,
						items: [
							{
								content: [p],
							}
						]
					};
					return list;

				} else if (type === "listItem") {
					const p = this.createItem("p");

					return {
						...copyFrom,
						type,
						id: this.nextID(),
						content: [p],
					};

				} else if (type === "tableCell") {
					const p = this.createItem("p");

					return {
						...copyFrom, 
						type,
						id: this.nextID(),
						content: [p],
					};

				} else if (type === "tableRow") {
					return {
						...copyFrom,
						type,
						id: this.nextID(),
						cells: [],
					};

				} else if (type === "table") {
					const tbl: Table = {
						...copyFrom,
						type,
						id: this.nextID(),
						// cellPadding: {
						// 	top: 3, right: 3, bottom: 3, left: 3,
						// },
						cols: [{
							widthPercent: 100,
						}],
						rows: [{
							cells: [{
								content: [this.createItem("p") as Paragraph]
							}]
						}]
					};
					return tbl;

				} else if (type === "hr") {
					return {
						...copyFrom,
						id: this.nextID(),
						type,
					}

				} else if (type === "group") {
					return {
						...copyFrom,
						type,
						id: this.nextID(),
						content: [this.createItem("p") as Paragraph]
					}

				} else if (type === "region") {
					const p = this.createItem("p") as Paragraph;
					p.bold = true;
					p.html = `<span
						class="field-token"
						contentEditable="false"
						id="${this.nextID()}"
						data-options='${encodeURIComponent(JSON.stringify({}))}'
					>field</span>`;

					return {
						...copyFrom,
						type,
						id: this.nextID(),
						content: [p]
					}

				} else if (type === "image") {
					return {
						...copyFrom,
						type,
						id: this.nextID(),
					}

				} else if (type === "sub-report") {
					return {
						...copyFrom,
						type,
						id: this.nextID(),
					}

				} else if (type === "sig-block") {
					return {
						...copyFrom,
						type,
						id: this.nextID(),
						content: [this.createItem("p") as Paragraph]
					}

				} else if (type === "toc") {
					return {
						...copyFrom,
						type,
						id: this.nextID(),
					}

				} else if (type === "tag") {
					return {
						...copyFrom,
						type,
						id: this.nextID(),
						content: [this.createItem("p") as Paragraph]
					}

				} else if (type === "pagebreak") {
					return {
						...copyFrom,
						type,
						id: this.nextID(),
					}

				} else if (type === "link") {
					return {
						...copyFrom,
						type,
						id: this.nextID(),
					}

				}

				return undefined;
			},

			addItem: function(item: Content, toArray: any[], relativeTo: Content, offset: number) {
				const i = toArray.indexOf(relativeTo);
				if (i >= 0) {
					toArray.splice(i+offset, 0, item);
				}
			},		
			
			getContentArray: function(item: Content) {
				return (item as any).content as AllContent[];
				// if (parent.type === "section") {
				// 	contentArray = (parent as Section).content;
				// } else if (parent.type === "listItem") {
				// 	contentArray = (parent as ListItem).content;
				// } else if ((parent.type === "header") || (parent.type === "footer")) {
				// 	contentArray = (parent as HeaderFooter).content;
				// }
			},

			addParagraph: function(before?: boolean) {
				// add paragraph directly after current focus and then set it to be focused
				if (!this.focus) return;

				const elementContext = this.getContext(this.focus.id!);
				if (!elementContext) return;

				const parent = elementContext.parent.def;

				let contentArray = this.getContentArray(parent);

				if (contentArray) {
					const p = this.createItem("p", this.focus) as Paragraph;
					if (p) {
						this.addItem(p, contentArray, elementContext.def, before ? 0 : 1);
						this.setFocus(p);
						this.refreshItem(parent);
					}

				}

			},

			refreshItem: function(item: Content) {
				this.broadcastToContent(item.id!, {
					type: DesignerMessages.REFRESH,
				});
			},

			deleteItem: function(item: Content) {
				const elementContext = this.getContext(item.id!);
				if (!elementContext) return;

				const parent = elementContext.parent.def;

				let contentArray = this.getContentArray(parent);
				if (!contentArray) return;

				let toFocus;
				let toRefresh;

				const i = contentArray.indexOf(item);
				if (i >= 0) {
					contentArray.splice(i, 1);

					// if there is nothing left in this content array then the parent has become completely empty
					// so remove it also
					if (contentArray.length === 0) {
						if (parent.type === "listItem") {
							const list = elementContext.parent.parent.def as List;
							const liIndex = list.items.indexOf(elementContext.parent.def as any);
							if (liIndex >= 0) {
								list.items.splice(liIndex, 1);
								toRefresh = list;

								if (liIndex > 0) {
									const liPrev = list.items[liIndex-1];
									toFocus = liPrev.content[liPrev.content.length-1];
								}
							}
						}

						if (parent.type === "region") {
							const section = elementContext.parent.def as Section;
							const index = section.content.indexOf(elementContext.def);
							section.content.splice(index, 1);
							toRefresh = section;
						}

					} else if (i > 0) {
						toFocus = contentArray[i-1];
						toRefresh = parent;

					} else {
						toRefresh = parent;

					}

					if (toRefresh) {
						this.refreshItem(toRefresh);
					}
				}

				// auto focus the item before it
				if (toFocus) {
					this.setFocus(toFocus);
				}
				
			},

			refreshRoot: function() {
				broadcast(this.channel, {
					type: DesignerMessages.REFRESH_ROOT
				});
			},

		};

		return value;
	}, [onSave, onCancel, def]);

	return (
		<DesignerContext.Provider value={state}>
			<div className="pdf-designer">
				<Toolbar />

				<Toolbox />

				<div className="structure-container">
					<Structure structure={props.def.data} />
				</div>

				<Inspector />

				<StatusBar />

				<BackgroundPdfLoader />
			</div>
		</DesignerContext.Provider>
	);
}

function prepareForSave(designer: DesignerState) {
	// make copy
	const copy = JSON.parse(JSON.stringify(designer.structure));

	// find all headings and paragraphs and convert html to segments
	function scan(o: Content) {
		// delete o.id;

		if (o.type === "p" || o.type === "heading") {
			// process html
			const p = o as Paragraph;

			const msg = {
				type: DesignerMessages.GET_SEGMENTS,
				segments: [] as any[],
			};
			designer.broadcastToContent(p.id!, msg);
			
			// NOTE: the segments are not exactly the format required, so produce correct structure
			const segments = [] as Segment[];
			for(let s of msg.segments) {
				segments.push({
					bold: s.format.bold,
					italic: s.format.italic,
					text: s.text.replace(/[\n\t]/g, ""),

					field: s.format.field,
				});
			}

			p.segments = segments;

			// remove the html
			delete p.html;

		} else if (o.type === "section") {
			const s = o as Section;
			for(let c of s.content) {
				scan(c);
			}
			if (s.header) scan(s.header);
			if (s.footer) scan(s.footer);

		} else if (o.type === "header" || o.type === "footer") {
			const hf = o as HeaderFooter;
			for(let c of hf.content) {
				scan(c);
			}

		} else if (o.type === "list") {
			const l = o as List;
			for(let li of l.items) {
				// delete li.id;

				for(let c of li.content) {
					scan(c);
				}
			}

		} else if (o.type === "table") {
			const t = o as Table;
			for(let r of t.rows) {
				// delete r.id;

				for(let cell of r.cells) {						
					for(let c of cell.content) {
						scan(c);
					}
				}
			}

		} else if ((o as any).content && (o as any).content.length > 0) {
			for(let c of (o as any).content) {
				scan(c);
			}	

		}
	}
	for(let s of copy.sections) {
		scan(s);
	}

	return copy;
}




